import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { usePersistentState, useFlashBackground } from '../../hooks'
import NotificationsRenderer from '../NotificationsRenderer'
import DispatchMap, { MapSelection, useMapSettings } from './DispatchMap'
import { selectMapDeviceLocations } from './selectMapDeviceLocations'
import DispatchSideBar from './DispatchSideBar'
import DispatchIncidentAlert from './DispatchIncidentAlert'
import DispatchPanel from './DispatchPanel'
import CreateIncidentPanel from './Panels/CreateIncident'
import OngoingIncidentsPanel from './Panels/OngoingIncidents'
import RespondingUnitsPanel from './Panels/RespondingUnits'
import AvailableUnitsPanel from './Panels/AvailableUnits'
import ChatPanel from './Panels/Chat'

import './styles.scss'
import { TrackService } from '../../services/TrackService'
import { useRest } from '../../hooks/api'
import { auth } from '../../firebase'

function panelTitle(activePanel) {
  switch (activePanel) {
    case 'new-incident':
      return 'Create a new incident'
    case 'ongoing-incidents':
      return 'Incidents'
    case 'responding-units':
      return 'Responding units'
    case 'available-units':
      return 'Duty status'
    case 'map-settings':
      return 'Map settings'
    default:
      return ''
  }
}

export default function ({
  availableUnits,
  deviceLocations,
  incidents,
  userTypes,
  dispatchBindings,
  profile
}) {
  const rest = useRest()
  const tracks = TrackService(rest)
  const [mapSettings, setMapSettings] = useMapSettings()
  const [activePanel, setActivePanel] = useState('ongoing-incidents')
  const [secondaryPanel, setSecondaryPanel] = useState()
  const [{ expanded }, setMenuState] = usePersistentState('dispatch-menu', {
    expanded: true
  })

  const [sidebarWidth, setSidebarWidth] = useState(1e10)
  const DEFAULT_USA_CENTER = { lat: 39.8283, lng: -98.5795 }
  const [mapCenter, setMapCenter] = useState(DEFAULT_USA_CENTER)
  const [mapZoom, setMapZoom] = useState(15)
  const [mapSelection, setMapSelection] = useState()
  const [customActiveChannel, setCustomActiveChannel] = useState()
  const [selectedIncident, setSelectedIncident] = useState(null)
  const [directionsDestination, setDirectionsDestination] = useState()
  const [apiLocationsByUserId, setApiLocationsByUserId] = useState({})
  const hasFetchedLocations = useRef(false)

  useEffect(() => {
    if (!availableUnits || !profile?.organization?.id) return
    if (hasFetchedLocations.current) return

    const onDutyUsers = Object.values(availableUnits).filter(
      (u) =>
        u.dutyStatus === true &&
        u.organization?.id === profile?.organization?.id
    )

    if (!onDutyUsers.length) return

    hasFetchedLocations.current = true

    const fetchUserLocations = async () => {
      try {
        const userIds = onDutyUsers.map((u) => u.id)

        const res = await tracks.getUsersLocationByIds(userIds)

        if (res.message === 'Success' && res.data) {
          const mapped = res.data.reduce((acc, item) => {
            acc[item.id] = [item.longitude, item.latitude]
            return acc
          }, {})

          setApiLocationsByUserId(mapped)
        }
      } catch (err) {
        console.error('get user location Error : ', err)
      }
    }

    fetchUserLocations()
  }, [availableUnits])

  const mergedDeviceLocations = useMemo(() => {
    const apiConverted = Object.entries(apiLocationsByUserId).reduce(
      (acc, [userId, loc]) => {
        // Carry the real user document, not a bare { id }. These entries are
        // seeded from availableUnits, so it is already loaded — and a bare id
        // has no organization to scope by and no displayName, which made
        // DeviceMarkersWrapper re-fetch every one of them on every render.
        acc[userId] = { user: availableUnits?.[userId] || { id: userId }, loc }
        return acc
      },
      {}
    )

    return {
      ...apiConverted,
      ...deviceLocations // live socket data overrides API
    }
  }, [apiLocationsByUserId, deviceLocations, availableUnits])

  // Two modes, mirroring mobile: own organisation, or the selected incident's
  // accepted responders. See selectMapDeviceLocations for why the map needs its
  // own view of this and the panels must keep the unfiltered one.
  const mapDeviceLocations = useMemo(
    () =>
      selectMapDeviceLocations(mergedDeviceLocations, selectedIncident, profile),
    [mergedDeviceLocations, selectedIncident, profile]
  )

  const directionsOrigin = useMemo(() => {
    const deviceData = Object.values(deviceLocations).find(
      ({ user }) => user?.id === profile.id
    )

    if (!deviceData) {
      return [41.54465187, -90.55797034]
      //      return null
    }

    return deviceData && deviceData.loc
  }, [deviceLocations, profile])

  // useEffect(() => {
  //   const fetchToken = async () => {
  //     if (!auth.currentUser) {
  //       return
  //     }
  //     const token = await auth.currentUser.getIdToken(true)
  //     try {
  //       const response = await tracks.startTrack(123, 123456)
  //       // if (response?.message == 'Success') {
  //       //   localStorage.setItem('Token', response?.data?.token)
  //       // }
  //     } catch (error) {
  //       console.error('Verify failed:', error)
  //     }
  //   }
  //   fetchToken()
  // }, [])

  useEffect(() => {
    if (mapSettings.defaultMapCenter) {
      setMapCenter(mapSettings.defaultMapCenter)
    } else if (
      mapCenter.lat === DEFAULT_USA_CENTER.lat &&
      mapCenter.lng === DEFAULT_USA_CENTER.lng
    ) {
      navigator.geolocation.getCurrentPosition(
        ({ coords: { latitude, longitude } }) =>
          setMapCenter({ lat: latitude, lng: longitude })
      )
    }

    if (mapSettings.defaultMapZoom) {
      setMapZoom(mapSettings.defaultMapZoom)
    }
  }, [mapSettings])

  const onMapClick = useCallback(
    ({ loc, placeId }) => {
      setMapSelection(new MapSelection(loc, placeId))
    },
    [setMapSelection]
  )

  const sidebarContainer = useRef(null)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (sidebarContainer.current) {
      const width = sidebarContainer.current.getBoundingClientRect().width
      if (width !== sidebarWidth) {
        setSidebarWidth(width)
      }
    }
  })

  const openSecondaryPanel = useCallback(
    (component, title) => {
      setMenuState({ expanded: true })
      setSecondaryPanel(null)

      if (!component) {
        return
      }

      setTimeout(() => {
        setSecondaryPanel(
          <DispatchPanel
            onHideClick={() => setSecondaryPanel(null)}
            style={{ width: '600px' }}
            title={title}>
            {component}
          </DispatchPanel>
        )
      }, 0)
    },
    [setSecondaryPanel, setMenuState]
  )

  const closeSecondaryPanel = useCallback(() => {
    setSecondaryPanel(null);
  }, []);


  const onIncidentCreated = useCallback((incident) => {
    setActivePanel(null)
    setMapSelection(null)
    setTimeout(() => setActivePanel('new-incident'), 250)
  }, [])

  useEffect(() => {
    setSecondaryPanel(null)
  }, [activePanel])

  const [addressSearchBoxRef, flashAddressSearchBox] = useFlashBackground()

  return (
    <>
      <div
        className='dispatch-sidebar-container'
        style={{ left: expanded ? 0 : -sidebarWidth + 16 }}
        ref={sidebarContainer}>
        <DispatchSideBar
          onItemClick={setActivePanel}
          activePanel={activePanel}
        />
        {activePanel && (
          <DispatchPanel
            title={activePanel !== 'chat' && panelTitle(activePanel)}
            onHideClick={() => setActivePanel(null)}
            style={{ width: activePanel === 'chat' && '' }}
            className={activePanel === 'chat' && 'chat-panel'}>
            {activePanel === 'new-incident' && (
              <CreateIncidentPanel
                mapSelection={mapSelection}
                userTypes={userTypes || []}
                profile={profile}
                dispatchBindings={dispatchBindings}
                onIncidentCreated={onIncidentCreated}
                addressSearchBoxRef={addressSearchBoxRef}
                flashAddressSearchBox={flashAddressSearchBox}
              />
            )}
            {activePanel === 'ongoing-incidents' && (
              <OngoingIncidentsPanel
                incidents={incidents}
                setActivePanel={setActivePanel}
                openSecondaryPanel={openSecondaryPanel}
                setMapCenter={setMapCenter}
                setMapZoom={setMapZoom}
                mapSelection={mapSelection}
                dispatchBindings={dispatchBindings}
                profile={profile}
                setCustomActiveChannel={setCustomActiveChannel}
                selectedIncident={selectedIncident}
                setSelectedIncident={setSelectedIncident}
                setDirectionsDestination={setDirectionsDestination}
                userTypes={userTypes || []}
              />
            )}
            {activePanel === 'responding-units' && (
              <RespondingUnitsPanel
                incidents={incidents}
                deviceLocations={deviceLocations}
                availableUnits={availableUnits}
                openSecondaryPanel={openSecondaryPanel}
                setMapCenter={setMapCenter}
              />
            )}
            {activePanel === 'available-units' && (
              <AvailableUnitsPanel
                availableUnits={availableUnits}
                openSecondaryPanel={openSecondaryPanel}
                setMapCenter={setMapCenter}
                mergedDeviceLocations={mergedDeviceLocations || {}}
              />
            )}
            <div
              style={{
                display: activePanel === 'chat' ? 'flex' : 'none',
                width: '100%'
              }}>
              <ChatPanel
                customActiveChannel={customActiveChannel}
                setCustomActiveChannel={setCustomActiveChannel}
              />
            </div>
          </DispatchPanel>
        )}
        {secondaryPanel}
        <div className='dispatch-sidebar-chevron'>
          <button
            type='button'
            className='button'
            onClick={() => setMenuState({ expanded: !expanded })}>
            <i className={`fa fa-chevron-${expanded ? 'left' : 'right'}`} />
          </button>
        </div>
      </div>
      <DispatchMap
        center={mapCenter}
        setMapCenter={setMapCenter}
        zoom={mapZoom}
        setMapZoom={setMapZoom}
        deviceLocations={mapDeviceLocations}
        incidents={incidents}
        mapSelection={mapSelection}
        onMapClick={onMapClick}
        openSecondaryPanel={openSecondaryPanel}
        closeSecondaryPanel={closeSecondaryPanel}
        selectedIncident={selectedIncident}
        setSelectedIncident={setSelectedIncident}
        leftMargin={expanded ? sidebarWidth : 16}
        addressSearchBoxRef={addressSearchBoxRef}
        directionsOrigin={directionsOrigin}
        directionsDestination={directionsDestination}
      />
      <NotificationsRenderer
        containerClassName='dispatch-notifications'
        containerStyle={{ left: expanded ? sidebarWidth : 16 }}
        itemClassName='notification'
      />
      <DispatchIncidentAlert incidents={incidents} />
    </>
  )
}
